import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Notify } from '../services/notificationService';
import { trackEvent } from '../utils/monitoring';
import { getStoredReferrerData } from '../utils/utmTracking';
import { CirraServiceType } from '../enums/auth';

/**
 * URL parameters from Auth0 email verification callback
 */
export interface EmailVerificationParams {
  success: string | null;
  code: string | null;
  message: string | null;
}

/**
 * Parse email verification parameters from the current URL
 */
export function parseEmailVerificationParams(): EmailVerificationParams {
  const params = new URLSearchParams(window.location.search);
  return {
    success: params.get('success'),
    code: params.get('code'),
    message: params.get('message'),
  };
}

/**
 * Check if the current URL parameters indicate an email verification callback
 */
export function isEmailVerificationCallback(
  params: EmailVerificationParams
): params is EmailVerificationParams & { message: string } {
  return (
    params.success === 'true' &&
    params.code === 'success' &&
    params.message !== null
  );
}

/**
 * Track email verification completion and clean up stored signup data
 */
export function trackEmailVerificationCompletion(): void {
  const signupData = getStoredReferrerData();
  const storedEmail = localStorage.getItem('signup_email');

  trackEvent('Email Verification Completed', {
    email_domain: storedEmail ? storedEmail.split('@')[1] : undefined,
    hasSignupAttribution: !!signupData,
    ...(signupData || {}),
  });

  // Clear the stored signup tracking IDs now that we've completed the signup journey
  localStorage.removeItem('signup_session_id');
  localStorage.removeItem('signup_correlation_id');
}

/**
 * Check if there's an account mismatch between the currently logged-in user
 * and the email that was just verified
 */
function checkAccountMismatch(currentUserEmail: string | undefined): {
  isMismatch: boolean;
  storedSignupEmail: string | null;
} {
  const storedSignupEmail = localStorage.getItem('signup_email');

  const isMismatch = !!(
    currentUserEmail &&
    storedSignupEmail &&
    currentUserEmail.toLowerCase() !== storedSignupEmail.toLowerCase()
  );

  return { isMismatch, storedSignupEmail };
}

interface UseEmailVerificationHandlerOptions {
  service?: CirraServiceType;
}

/**
 * Hook to handle Auth0 email verification redirect.
 * When a user clicks the verification link in their email, Auth0 redirects back
 * with success/code/message parameters. This hook detects that, tracks the event,
 * shows a success notification, and redirects to the appropriate page.
 *
 * If the user is logged in as a different account than the one being verified,
 * redirects to an account mismatch page instead of the normal success page.
 */
export function useEmailVerificationHandler(
  options: UseEmailVerificationHandlerOptions = {}
): void {
  const { service = CirraServiceType.MCP } = options;
  const navigate = useNavigate();
  const { user } = useAuth0();

  useEffect(() => {
    const params = parseEmailVerificationParams();

    if (!isEmailVerificationCallback(params)) {
      return;
    }

    // Check for account mismatch before processing
    const { isMismatch, storedSignupEmail } = checkAccountMismatch(user?.email);

    if (isMismatch && storedSignupEmail) {
      // User is logged in as a different account - redirect to mismatch page
      // Clear query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Track the mismatch event (not the completion event)
      trackEvent('Email Verification Account Mismatch', {
        current_email_domain: user?.email?.split('@')[1],
        verified_email_domain: storedSignupEmail.split('@')[1],
      });

      // Navigate to mismatch page with state
      navigate('/email-verified-mismatch', {
        replace: true,
        state: {
          currentEmail: user?.email,
          verifiedEmail: storedSignupEmail,
          service,
        },
      });
      return;
    }

    // Normal flow - user verified their own account (or no mismatch detected)
    // Clear query params from URL to prevent duplicate processing in React 18 Strict Mode
    window.history.replaceState({}, document.title, window.location.pathname);

    trackEmailVerificationCompletion();
    // Clear signup_email now that verification is complete to prevent stale data
    // from causing false mismatch detections or wrong emails in future sessions
    localStorage.removeItem('signup_email');
    Notify.success(params.message);
    navigate('/email-verified', { replace: true });
  }, [navigate, user?.email, service]);
}
